import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { GridPaginationModel } from '@mui/x-data-grid';
import DataGridWithLoading, { ResponsiveGridColDef } from 'components/DataGrid';
import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import {
  PAGINATION_DEFAULT_PAGE_SIZE,
  PAGINATION_PAGE_SIZES
} from 'config';
import { BasicDisplayUnits, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import {
  UnitSystem,
  WearDataDto,
  useNodeVolumeRecordsBySpoolIdQuery
} from 'providers/api';
import React from 'react';
import { round } from 'utils';
import { UnitSystemValue } from '../StopePanelPage/RecipesTab/UnitSystemDetail';
import { calculateWearRateBetweenPointsFor } from '../canvas/PCFunctions/wearCalculations';

const positionsAsArray = (wearData: WearDataDto) => (
  [
    wearData.position1,
    wearData.position2,
    wearData.position3,
    wearData.position4,
    wearData.position5,
    wearData.position6,
    wearData.position7,
    wearData.position8,
  ]
);

const calculateMeanThickness = (wearData: WearDataDto) => {
    const meanComponents = positionsAsArray(wearData).reduce((meanComponentsAcc, position) => ({
    count: meanComponentsAcc.count + (position ? 1 : 0),
    sum: meanComponentsAcc.sum + (position ?? 0),
  }),
  { count: 0, sum: 0 });

  return meanComponents.count > 0 ? meanComponents.sum / meanComponents.count : 0;
};

const displayWearData = (wearData: WearDataDto, unitSystemPreference: UnitSystem) => (
  positionsAsArray(wearData)
    .map((positionValue) => {
      if (positionValue === undefined) return 'x';
      return unitSystemPreference === UnitSystem.Metric
        ? positionValue
        : round(unitLabels[BasicDisplayUnits.SmallDistance].imperial.conversion(positionValue), 2);
    })
    .join('|')
);

interface WearDataTableMenuProps {
  wearData: WearDataDto;
  onEdit: (wearDataId: string) => void;
  onDelete: (wearData: WearDataDto) => void;
}

const WearDataTableMenu = ({ wearData, onEdit, onDelete }: WearDataTableMenuProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(wearData.entityId);
    setAnchorEl(null);
  };

  const handleDelete = () => {
    onDelete(wearData);
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton color="inherit" aria-controls={`wear-data-actions-menu-${wearData.inspectionPointId}`} aria-haspopup="true" onClick={handleClick} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu
        id={`wear-data-actions-menu-${wearData.inspectionPointId}`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit"><Trans id="Edit">Edit</Trans></Typography>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteForeverIcon color="error" fontSize="small" />
          </ListItemIcon>
          <Typography color="error" variant="inherit"><Trans id="Delete">Delete</Trans></Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

interface WearDataTableProps {
  data: WearDataDto[] | undefined;
  page: number;
  pageSize: number;
  totalItems: number | undefined;
  onPaginationModelChange: (paginationModel: GridPaginationModel) => void,
  onEdit: (wearDataId: string) => void;
  onDelete: (client: WearDataDto) => void;
  loading: boolean;
  loadingNew: boolean;
  searchTerm: string | null;
  unitSystemPreference: UnitSystem;
  mineModelId: string;
  spoolId: string;
}

const WearDataTable = ({
  data,
  onPaginationModelChange,
  onEdit,
  onDelete,
  loadingNew,
  loading,
  page,
  pageSize = PAGINATION_DEFAULT_PAGE_SIZE,
  totalItems = 0,
  searchTerm,
  unitSystemPreference,
  mineModelId,
  spoolId,
}: WearDataTableProps) => {
  const pageIndex = data ? (page - 1) : 0;
  const rows = data ?? [];

  const { data: throughputRecords } = useNodeVolumeRecordsBySpoolIdQuery(
    {
      mineModelId,
      spoolId,
    },
    {
      enabled: !!spoolId && !!mineModelId,
    },
  );

  const getWearRate = (currentWearData: WearDataDto, rowIndex: number): number | null => {
    if (!throughputRecords || !rows[rowIndex + 1]) return null;

    const previousWearData = rows[rowIndex + 1];
    const rate = calculateWearRateBetweenPointsFor(
      currentWearData,
      previousWearData,
      throughputRecords,
      spoolId,
      (wear) => wear.average ?? null,
    );
    return rate;
  };

  const columns: ResponsiveGridColDef[] = [
    {
      field: 'inspectionDate',
      headerName: 'Inspection\nDate',
      resizable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      headerAlign: 'center',
      mobileConfig: {
        width: 200,
        isVisible: true,
      },
      renderHeader: () => (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', textAlign: 'center', lineHeight: 1.2 }}>
          {i18n._('Inspection\nDate')}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const wearData = row as WearDataDto;
        const date = wearData.inspectionDate;
        const day = date.dayOfMonth();
        const month = date.month().toString().substring(0, 3);
        const year = date.year();
        return `${day} ${month} ${year}`;
      },
    },
    {
      field: 'points',
      headerName: 'Avg.\nThickness',
      sortable: false,
      filterable: false,
      resizable: false,
      width: 120,
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', textAlign: 'center', lineHeight: 1.2 }}>
          {i18n._('Avg. Remaining\nThickness')}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip arrow title={displayWearData(row as WearDataDto, unitSystemPreference)} aria-label="remaining thickness measurements">
          <Box
            sx={{
              width: '100%',
              height: '100%',
            }}
          >
            <UnitSystemValue
              value={calculateMeanThickness(row as WearDataDto)}
              unitInfo={unitLabels[BasicDisplayUnits.SmallDistance]}
              unitSystem={unitSystemPreference}
              decimalPlaces={2}
              customUnitFields={[]}
            />
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'wearRate',
      headerName: 'Wear Rate\n(mm/Mt)',
      sortable: false,
      filterable: false,
      resizable: false,
      width: 120,
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', textAlign: 'center', lineHeight: 1.2 }}>
          {i18n._('Wear Rate\n(mm/Mt)')}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const rowIndex = rows.findIndex((r) => r.entityId === (row as WearDataDto).entityId);
        const wearRate = getWearRate(row, rowIndex);
        return wearRate !== null ? round(wearRate, 3) : '-';
      },
    },
    {
      field: '',
      sortable: false,
      filterable: false,
      resizable: false,
      width: 40,
      renderCell: ({ row }) => (<WearDataTableMenu wearData={row as any} onEdit={onEdit} onDelete={onDelete} />),
    },
  ];

  return (
    <DataGridWithLoading
      density="compact"
      loading={loading}
      loadingNew={loadingNew}
      searchTerm={searchTerm}
      searchFields={['name']}
      disableColumnMenu
      autoHeight
      idProp="entityId"
      rows={rows}
      columns={columns}
      pagination
      paginationMode="server"
      paginationModel={{
        page: pageIndex,
        pageSize,
      }}
      onPaginationModelChange={onPaginationModelChange}
      pageSize={pageSize}
      rowCount={totalItems}
      pageSizeOptions={PAGINATION_PAGE_SIZES}
    />
  );
};

export default WearDataTable;
